import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import {
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  EventNote as NoteIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import moment from "moment";
import ClinicalNotesList from "./ClinicalNotesList";

export default function EncounterTimeline() {
  const { patientId } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Build query string - only include patient if it exists
  const queryParams = new URLSearchParams();
  if (patientId) {
    queryParams.append("patient", patientId);
  }
  if (startDate) {
    queryParams.append("start_date", startDate);
  }
  if (endDate) {
    queryParams.append("end_date", endDate);
  }
  const queryString = queryParams.toString();
  const apiUrl = `encounters/${queryString ? `?${queryString}` : ""}`;

  const { data: encounters, isLoading, isError, error } = useFetchObjects(
    ["encounters", "patient", patientId, startDate, endDate],
    apiUrl,
    token
  );

  const encounterList = encounters?.results || [];
  const [expandedEncounters, setExpandedEncounters] = useState({});

  const getEncounterIcon = (type) => {
    switch (type) {
      case 1: // Consultation
        return <AssignmentIcon />;
      case 4: // Emergency
        return <HospitalIcon />;
      default:
        return <NoteIcon />;
    }
  };

  const getEncounterColor = (type) => {
    switch (type) {
      case 1:
        return "primary";
      case 4:
        return "error";
      case 3:
        return "info";
      default:
        return "grey";
    }
  };

  const getEncounterColorValue = (type) => {
    const colors = {
      1: "#1976d2", // primary blue
      4: "#d32f2f", // error red
      3: "#0288d1", // info blue
    };
    return colors[type] || "#9e9e9e"; // default grey
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load encounters"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
          Patient Encounter Timeline
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {encounterList.length === 0 ? (
          <Alert severity="info">No encounters found</Alert>
        ) : (
          <Stack spacing={3}>
            {encounterList.map((encounter, index) => (
              <Box key={encounter.id} sx={{ display: "flex", gap: 2 }}>
                {/* Timeline line and dot */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: getEncounterColorValue(encounter.encounter_type),
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    {getEncounterIcon(encounter.encounter_type)}
                  </Box>
                  {index < encounterList.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        bgcolor: "divider",
                        minHeight: 50,
                      }}
                    />
                  )}
                </Box>

                {/* Date column */}
                <Box sx={{ minWidth: 120, pt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                    {moment(encounter.encounter_date).format("MMM DD, YYYY")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(encounter.encounter_date).format("HH:mm")}
                  </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="h6">
                          {encounter.encounter_type_display}
                        </Typography>
                        <Chip
                          label={encounter.status_display}
                          size="small"
                          color={encounter.status === 2 ? "success" : "default"}
                        />
                      </Box>
                      {encounter.department_name_display && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Department: {encounter.department_name_display}
                        </Typography>
                      )}
                      {encounter.provider_name_display && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Provider: {encounter.provider_name_display}
                        </Typography>
                      )}
                      {encounter.chief_complaint && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Chief Complaint:</strong> {encounter.chief_complaint}
                        </Typography>
                      )}
                      {encounter.diagnosis && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Diagnosis:</strong> {encounter.diagnosis}
                        </Typography>
                      )}
                      {encounter.notes && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Notes:</strong> {encounter.notes}
                        </Typography>
                      )}
                      {encounter.clinical_notes_count > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Clinical Notes:</strong> {encounter.clinical_notes_count}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setExpandedEncounters((prev) => ({
                              ...prev,
                              [encounter.id]: !prev[encounter.id],
                            }));
                          }}
                          startIcon={expandedEncounters[encounter.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {expandedEncounters[encounter.id]
                            ? "Hide Clinical Notes"
                            : `View Clinical Notes (${encounter.clinical_notes_count || 0})`}
                        </Button>
                        {expandedEncounters[encounter.id] && (
                          <Box sx={{ mt: 2 }}>
                            <ClinicalNotesList encounter={encounter} />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
